/**
 * Authentication module for the Fyncall Widget
 * Handles token management, refresh, and storage
 */

import { getBaseUrl } from './config.js';

// Token state
let currentToken = null;
let tokenExpiresAt = null;
let refreshTimer = null;
let widgetId = null;

// Refresh token when 20% of lifetime remains
const REFRESH_THRESHOLD = 0.2;

/**
 * Initialize auth with configuration
 * @param {Object} options - Auth options from loader
 * @param {string} options.token - JWT token
 * @param {number} options.tokenExpiresAt - Token expiration timestamp
 * @param {string} options.widgetId - Widget ID for token refresh
 */
export function initAuth(options = {}) {
    widgetId = options.widgetId || null;

    if (options.token) {
        currentToken = options.token;
        tokenExpiresAt = options.tokenExpiresAt || null;
        scheduleTokenRefresh();
    }

    console.log('[FyncallAuth] Initialized:', {
        hasToken: !!currentToken,
        widgetId: widgetId ? widgetId.substring(0, 8) + '...' : null
    });
}

/**
 * Check if authenticated (has valid token)
 * @returns {boolean} Whether authenticated
 */
export function isAuthenticated() {
    return !!currentToken;
}

/**
 * Get current token
 * @returns {string|null} Current JWT token
 */
export function getToken() {
    return currentToken;
}

/**
 * Check if token is valid (not expired)
 * @returns {boolean} Whether token is valid
 */
export function isTokenValid() {
    if (!currentToken || !tokenExpiresAt) {
        return false;
    }
    // Consider token invalid if less than 30 seconds remaining
    return Date.now() < (tokenExpiresAt - 30000);
}

/**
 * Check if token needs refresh
 * @returns {boolean} Whether token should be refreshed
 */
export function needsRefresh() {
    if (!currentToken || !tokenExpiresAt) {
        return false;
    }
    const remaining = tokenExpiresAt - Date.now();
    const assumedLifetime = 3600 * 1000; // 1 hour default token lifetime
    return remaining < (assumedLifetime * REFRESH_THRESHOLD);
}

/**
 * Refresh the token
 * @returns {Promise<boolean>} Whether refresh was successful
 */
export async function refreshToken() {
    if (!currentToken) {
        console.warn('[FyncallAuth] Cannot refresh: no token');
        return false;
    }

    try {
        const baseUrl = getBaseUrl();
        const refreshUrl = `${baseUrl}/api/v1/widget/auth/refresh`;

        const headers = {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        };

        if (baseUrl.includes('ngrok')) {
            headers['ngrok-skip-browser-warning'] = 'true';
        }

        const response = await fetch(refreshUrl, {
            method: 'POST',
            headers,
            body: JSON.stringify({ token: currentToken })
        });

        if (!response.ok) {
            console.error('[FyncallAuth] Token refresh failed:', response.status);
            return false;
        }

        const data = await response.json();
        currentToken = data.token;
        tokenExpiresAt = Date.now() + (data.expires_in * 1000);

        // Update global Fyncall object
        if (window.Fyncall) {
            window.Fyncall._token = currentToken;
            window.Fyncall._tokenExpiresAt = tokenExpiresAt;
        }

        console.log('[FyncallAuth] Token refreshed, expires in:', data.expires_in, 'seconds');
        scheduleTokenRefresh();
        return true;
    } catch (error) {
        console.error('[FyncallAuth] Token refresh error:', error);
        return false;
    }
}

/**
 * Schedule automatic token refresh
 */
function scheduleTokenRefresh() {
    // Clear any existing timer
    if (refreshTimer) {
        clearTimeout(refreshTimer);
        refreshTimer = null;
    }

    if (!tokenExpiresAt) {
        return;
    }

    // Calculate when to refresh (when 20% of lifetime remains)
    const now = Date.now();
    const remaining = tokenExpiresAt - now;

    if (remaining <= 0) {
        console.warn('[FyncallAuth] Token already expired');
        return;
    }

    // Refresh when 80% of time has elapsed
    const refreshIn = remaining * (1 - REFRESH_THRESHOLD);

    console.log('[FyncallAuth] Scheduling token refresh in', Math.round(refreshIn / 1000), 'seconds');

    refreshTimer = setTimeout(async () => {
        console.log('[FyncallAuth] Auto-refreshing token...');
        const success = await refreshToken();
        if (!success) {
            console.error('[FyncallAuth] Auto-refresh failed, widget may need re-initialization');
        }
    }, refreshIn);
}

/**
 * Clear token and stop refresh timer
 */
export function clearToken() {
    currentToken = null;
    tokenExpiresAt = null;

    if (refreshTimer) {
        clearTimeout(refreshTimer);
        refreshTimer = null;
    }

    if (window.Fyncall) {
        window.Fyncall._token = null;
        window.Fyncall._tokenExpiresAt = null;
    }
}

/**
 * Get authorization header value
 * @returns {string|null} Authorization header value or null
 */
export function getAuthHeader() {
    if (currentToken) {
        return `Bearer ${currentToken}`;
    }
    return null;
}

/**
 * Get headers for API requests
 * @returns {Object} Headers object
 * @throws {Error} If no token is available
 */
export function getAuthHeaders() {
    if (!currentToken) {
        throw new Error('No auth token available. Widget must be initialized with a token.');
    }

    const headers = {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${currentToken}`
    };

    // Add ngrok bypass if needed
    const baseUrl = getBaseUrl();
    if (baseUrl && baseUrl.includes('ngrok')) {
        headers['ngrok-skip-browser-warning'] = 'true';
    }

    return headers;
}

/**
 * Handle 401 response - attempt token refresh
 * @returns {Promise<boolean>} Whether re-auth was successful
 */
export async function handle401() {
    console.log('[FyncallAuth] Received 401, attempting token refresh...');
    return await refreshToken();
}
